/* import __COLOCATED_TEMPLATE__ from './in-app-messaging-connect.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { tracked } from '@glimmer/tracking';
import { restartableTask, dropTask } from 'ember-concurrency-decorators';
import type IntlService from 'ember-intl/services/intl';
import type { TaskGenerator } from 'ember-concurrency';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import type NotificationsService from 'embercom/services/notifications-service';
import { taskFor } from 'ember-concurrency-ts';
interface Args {}

export default class StandaloneSalesforceInAppMessagingConnect extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;
  @service declare intl: IntlService;

  @tracked hostname: string | undefined;
  @tracked deploymentId: string | undefined;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    if (this.salesforceConfig.isPendingSetup) {
      return;
    }

    let { hostname, deploymentId } = this.salesforcePlatform.salesforceInAppMessagingConfig!;
    this.hostname = hostname || undefined;
    this.deploymentId = deploymentId || undefined;
  }

  get salesforcePlatform() {
    return this.finStandaloneService.salesforcePlatform;
  }

  get salesforceConfig(): SalesforceConfiguration {
    return this.salesforcePlatform.salesforceConfig!;
  }

  get isFormValid(): boolean {
    return !!this.hostname && !!this.deploymentId;
  }

  get isSaveRunning(): boolean {
    return taskFor(this.save).isRunning;
  }

  get isRevokeRunning(): boolean {
    return taskFor(this.revoke).isRunning;
  }

  @restartableTask
  *save(): TaskGenerator<void> {
    try {
      Object.assign(this.salesforcePlatform.salesforceInAppMessagingConfig!, {
        hostname: this.hostname,
        deploymentId: this.deploymentId,
        organizationId: this.salesforcePlatform.salesforceConfig!.organisationId,
      });
      yield this.salesforcePlatform.salesforceInAppMessagingConfig!.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.salesforce.setup.in-app-messaging-connect.save-success'),
      );
    } catch (error) {
      this.salesforcePlatform.salesforceInAppMessagingConfig!.rollbackAttributes();
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('standalone.salesforce.setup.in-app-messaging-connect.save-error'),
      });
    }
  }

  @dropTask
  *revoke(): TaskGenerator<void> {
    try {
      yield this.salesforcePlatform.salesforceInAppMessagingConfig!.revoke();
      this.hostname = undefined;
      this.deploymentId = undefined;
      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.salesforce.setup.in-app-messaging-connect.revoke-success'),
      );
    } catch (error) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('standalone.salesforce.setup.in-app-messaging-connect.revoke-error'),
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::Setup::InAppMessagingConnect': typeof StandaloneSalesforceInAppMessagingConnect;
    'standalone/salesforce/setup/in-app-messaging-connect': typeof StandaloneSalesforceInAppMessagingConnect;
  }
}
